import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IColumnTypes } from 'components/spreadsheet/types'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import MultilinkCellModal from 'components/spreadsheet/components/modal/views/multilink'
import { transformStringToCorrectFormat } from 'components/spreadsheet/helpers/paste'
import { Visible } from 'components/icons'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'

interface Props {
  value: string[] | null
  align: 'center' | 'left' | 'right'
  color: string
  kind: IColumnTypes
  readOnly: boolean
  isAdmin: boolean
  isContributor: boolean
  editing: boolean
  rowHeight: number
  selected: boolean
  validation: boolean
  setCellValue: (value: string[], onSuccess: () => void, onError: () => void) => void
  isExpanded?: boolean
  // Called to leave edit mode when the manage-links modal (opened because the cell
  // entered edit mode with existing links) is closed. Without it the effect below
  // would immediately reopen the modal.
  onExitEditing?: () => void
}

const styles = cellStyles()

// Parse typed/pasted text into an array of links, reusing the exact same delimiter
// parsing as the bulk paste path (`transformStringToCorrectFormat` for 'multilink',
// which splits on `,` `;` `|` via `splitByDelimiters`) so inline entry and Ctrl+V
// behave identically. Empty/whitespace-only entries (e.g. from a trailing delimiter)
// are dropped so we never save blank links.
const parseLinksInput = (text: string): string[] => {
  const trimmed = text.trim()
  if (!trimmed) return []

  let parsed: unknown
  try {
    parsed = transformStringToCorrectFormat(trimmed, 'multilink')
  } catch {
    // Invalid input (e.g. JSON like [1,2] whose items aren't strings). Match the bulk-paste
    // path, which rejects rather than saving it — commit nothing. Non-JSON input never
    // throws (it falls through to delimiter splitting inside transformStringToCorrectFormat).
    return []
  }

  if (!Array.isArray(parsed)) return []
  return (parsed as string[]).map((link) => link.trim()).filter((link) => link.length > 0)
}

const MultiLink: React.FC<Props> = ({
  value,
  align,
  color,
  readOnly,
  isAdmin,
  isContributor,
  editing,
  rowHeight,
  selected,
  validation,
  setCellValue,
  isExpanded,
  onExitEditing
}) => {
  const [multilinkModal, setMultilinkModal] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  // Guards against committing the same input twice — e.g. Enter commits and then the
  // resulting unmount fires a blur that would otherwise commit again — and lets Escape
  // suppress the trailing blur-commit entirely.
  const committedRef = useRef<boolean>(false)

  const hasLinks = (value ? value : []).length > 0

  // Inline editing is only offered for empty cells. Cells that already hold links keep
  // the modal as the management path (double-click opens it), because round-tripping
  // existing links through a delimited string would be lossy for URLs that legitimately
  // contain `,`, `;` or `|`.
  // `!multilinkModal` guards the case where the modal is open (cell had links) and the
  // user deletes them all: without it `hasLinks` flipping false would swap the open modal
  // for the inline input mid-interaction.
  const inlineEditing = editing && !readOnly && !isExpanded && !hasLinks && !multilinkModal

  // Entering edit mode on a cell that already holds links (via double-click or Space)
  // can't use the inline input — round-tripping existing links through a delimited
  // string is lossy — so open the manage-links modal instead.
  const editingWithLinks = editing && !readOnly && !isExpanded && hasLinks

  useEffect(() => {
    if (inlineEditing) {
      // Allow a new commit; leave inputValue intact so a char that triggered edit mode isn't
      // wiped (cleared on exit instead).
      committedRef.current = false
    } else {
      setInputValue('')
    }
  }, [inlineEditing])

  useEffect(() => {
    if (editingWithLinks) setMultilinkModal(true)
  }, [editingWithLinks])

  const handleModalOpenChange = (open: boolean) => {
    setMultilinkModal(open)
    // Closing a modal that was opened by entering edit mode must also leave edit mode,
    // otherwise the effect above reopens it immediately. Guard on `editing` so closing a
    // modal opened directly (eye icon / double-click on a non-selected cell) is a no-op.
    if (!open && editing) onExitEditing?.()
  }

  useLayoutEffect(() => {
    if (inlineEditing) inputRef.current?.focus()
  }, [inlineEditing])

  const commit = () => {
    if (committedRef.current) return
    committedRef.current = true
    const links = parseLinksInput(inputValue)
    if (links.length === 0) return
    setCellValue(
      links,
      () => {},
      () => {}
    )
  }

  if (inlineEditing) {
    return (
      <input
        className={styles.editing({ focused: true, class: 'cell-value-editing' })}
        data-focussed
        data-testid="multilink-cell-input"
        value={inputValue}
        placeholder="Paste or type link(s)"
        style={{ height: `${getRowHeightVariable(rowHeight) - 2}px` }}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !(event.ctrlKey || event.altKey || event.metaKey)) {
            commit()
          } else if (event.key === 'Tab') {
            commit()
          } else if (event.key === 'Escape') {
            // Discard: suppress the blur-commit that fires when the input unmounts.
            committedRef.current = true
          }
        }}
        onBlur={commit}
        ref={inputRef}
      />
    )
  }

  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value' })}
      data-focussed={selected || undefined}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        height: getRowHeightVariable(rowHeight) - 2,
        width: `var(--cell-width)`,
        maxWidth: `var(--cell-width)`
      }}
      onDoubleClick={(event: React.MouseEvent) => {
        if (readOnly || isExpanded) {
          event.stopPropagation()
          return
        }
        if (hasLinks) {
          // Existing links: keep the modal as the management path. Stop propagation so
          // the parent Cell doesn't also flip into (a no-op) editing state.
          event.stopPropagation()
          setMultilinkModal(true)
          return
        }
        // Empty & editable: let the event bubble so the parent Cell enters edit mode,
        // which re-renders this component into the inline input above.
      }}
    >
      <div className={styles.content({ validation, class: 'truncate flex' })}>
        <div className="w-full inline-flex truncate justify-between pr-[8px] border-0">
          <span className="self-center">{`${(value ? value : []).length} link(s)`}</span>
          {(selected || isExpanded) && (
            <div className="self-center cursor-pointer" onClick={() => setMultilinkModal(true)}>
              <Visible className="h-6 w-6" />
            </div>
          )}
        </div>
      </div>
      {multilinkModal && (
        <MultilinkCellModal
          id="multilink-cell-modal"
          open={multilinkModal}
          setOpen={handleModalOpenChange}
          links={value ? value : []}
          setCellValue={setCellValue}
          isAdmin={isAdmin}
          isContributor={isContributor}
        />
      )}
    </div>
  )
}

export const MultiLinkCell = MultiLink
